import { useEffect, useState } from "react";

import { Combobox, ComboboxInput, ComboboxOption, ComboboxOptions } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import normalizeString from "../../../utils/normaliseString";
import { anchoredPanelProps, comboboxPanelClasses } from "./anchoredPanel";

export default function ComboBoxMultipleInput({ label = "", options, searchField, valueField = "", register, setValue, registerName, defaultOption = false, initialValue = "", size = "md", classes = "", placeholder = "", disabled = false, returnObjects = false }) {
  const { t } = useTranslation("common");
  const [selected, setSelected] = useState([]);
  const [query, setQuery] = useState('')

  const sizeClasses = {
    sm: "px-2 py-1 text-sm",
    md: "px-3 py-2 text-sm",
    lg: "px-4 py-3 text-base"
  }

  //Search the options based on the name or the ID
  const safeOptions = options ?? [];

  const filteredOptions =
    query === ''
      ? safeOptions
      : safeOptions.filter((option) =>
        normalizeString(option[searchField])?.includes(normalizeString(query)) ||
        option.id?.toString().includes(query)
      );

  const handleSelect = (option) => {
    setSelected(option);
    // console.log(option)
    if (returnObjects) {
      setValue(registerName, option);
    } else if (valueField != "") {
      //This is wrong, it should be option.map((option) => option[valueField]).join(', ')) - need to test this doesn't break anything to fix this
      setValue(registerName, option.map((option) => option[valueField].join(', ')));
    } else {
      setValue(registerName, option.filter(Boolean).map((option) => option[searchField]).join(', '));
    }
  }

  useEffect(() => {

    if (defaultOption && safeOptions.length > 0) {
      // console.log(options)
      setSelected([safeOptions[0]]);
      // setValue(registerName, options[0])
    }

    if (initialValue !== "") {
      //Take comma separated values and convert them to an array of options
      // console.log(initialValue)
      //Any value that no longer exists in the option list is dropped - keeping an undefined
      //in `selected` blows up displayValue below
      const initialOptions = initialValue.split(',')
        .map((value) => value.trim())
        .map((value) => (options ?? []).find(option => option[searchField]?.toLowerCase() === value?.toLowerCase() || option.id?.toLowerCase() === value?.toLowerCase()))
        .filter((option) => option !== undefined);
      // console.log(initialOptions)

      if (initialOptions.length === 0) {
        // console.log('No options found')
      } else {
        setSelected(initialOptions);
        setValue(registerName, initialOptions.map((option) => option[searchField]).join(', '));
      }

    }
  }, [])

  // console.log(searchField, valueField, registerName, initialValue, selected)

  return (
    <div>
      {label &&
        <label>
          <span className="text-sm font-medium text-gray-500">{label}</span>
        </label>
      }
      <Combobox value={selected} onChange={handleSelect} multiple onClose={() => setQuery('')}>
        <div className={`relative mt-1 ${classes}`}>
          <div className="relative w-full cursor-default overflow-hidden bg-white text-left sm:text-sm ">
            <ComboboxInput
              className={`w-full text-gray-900 border-1 border-gray-300 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 ${sizeClasses[size]}`}
              displayValue={(options) =>
                (options ?? []).filter(Boolean).map((option) => option[searchField] ?? "").join(', ')}
              onChange={(event) => setQuery(event.target.value)}
              autoComplete="off"
              disabled={disabled}
              placeholder={placeholder}
            />
            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronUpDownIcon
                className="h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </Combobox.Button>
          </div>
          <ComboboxOptions
            {...anchoredPanelProps}
            className={`${comboboxPanelClasses} bg-white py-1 text-base ring-1 ring-gray-400 ring-opacity-5 sm:text-sm data-[anchor~=bottom]:rounded-b-md data-[anchor~=top]:rounded-t-md`}
          >
            {filteredOptions.length === 0 && query !== '' ? (
              <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                {t("Nothing found.")}
              </div>
            ) : (
              filteredOptions.slice(0, 100).map((option) => (
                <ComboboxOption
                  key={option.id ?? option[searchField]}
                  className={({ active }) =>
                    `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-primary-600 text-white' : 'text-gray-900'
                    }`
                  }
                  value={option}
                >
                  {({ selected, active }) => (
                    <>
                      <span
                        key={option.id}
                        className={`block truncate ${selected ? 'font-medium' : 'font-normal'
                          }`}
                      >
                        {option[searchField]}
                      </span>
                      {selected ? (
                        <span
                          key={option.id}
                          className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-primary-600'
                            }`}
                        >
                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                        </span>
                      ) : null}
                    </>
                  )}
                </ComboboxOption>
              ))
            )}
          </ComboboxOptions>
        </div>
      </Combobox>
    </div>
  )
}